/**
 * WhosOnTheFloor — answers "who owns what right now?" without
 * navigating to the Zone Sheet. Reads the existing
 * `sheet.crew_assignments[]` array (zone_sheets endpoint already
 * fetched at the dashboard level) and the `coverage-tasks/summary`
 * payload to surface each crew row's task progress (X/Y verified)
 * with a green/yellow/red dot so the manager can read the floor in
 * one glance.
 *
 * "Right Now" toggle filters to crew whose `shift` string overlaps
 * the current store-local clock; "Full Day" shows everyone on the
 * sheet. The shift string is free-form ("10a-3p", "12pm-5pm",
 * "530pm-1030pm" …) so the parser is forgiving — anything we can't
 * parse falls through to "include in Right Now" because hiding a
 * real assignee is worse than showing a stale one.
 */
import { useMemo, useState } from "react";
import { Clock, Users, ArrowRight } from "@phosphor-icons/react";
import EmptyStateChip from "./EmptyStateChip";

const STORE_TZ = "America/Denver";

/** Parse "10a-3p", "12PM-5PM", "530pm-1030pm", "10:30am-3pm" → [start, end] minutes-from-midnight, or null. */
function parseShiftRange(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  const m = s.match(/^([0-9]{1,2})(?::?([0-9]{2}))?(a|am|p|pm)?-([0-9]{1,2})(?::?([0-9]{2}))?(a|am|p|pm)?$/);
  if (!m) return null;
  const parseSide = (h, mm, ampm, otherAmpm) => {
    let hr = parseInt(h, 10);
    const min = parseInt(mm || "0", 10);
    // If this side has no am/pm, inherit from the other side (e.g. "12-5pm" → both pm)
    const ap = ampm || otherAmpm;
    if (ap) {
      const isPm = ap.startsWith("p");
      if (hr === 12) hr = isPm ? 12 : 0;
      else if (isPm) hr += 12;
    }
    if (hr > 23) hr = 23;
    return hr * 60 + min;
  };
  const start = parseSide(m[1], m[2], m[3], m[6]);
  let end = parseSide(m[4], m[5], m[6], m[3]);
  // If end <= start, assume it crosses noon (e.g. "10-3" w/ no am/pm); push end by 12h.
  if (end <= start) end += 12 * 60;
  return [start, end];
}

function nowMinutesInStoreTz() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: STORE_TZ,
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === "hour").value, 10) % 24;
  const mm = parseInt(parts.find((p) => p.type === "minute").value, 10);
  return h * 60 + mm;
}

function isCrewOnFloorNow(shift, nowMin) {
  const range = parseShiftRange(shift);
  if (!range) return true; // forgiving: if we can't parse, include them
  const [start, end] = range;
  return nowMin >= start && nowMin <= end;
}

/** Aggregate task progress for one crew member from the summary feed. */
function progressFor(crewId, summary) {
  const items = (summary?.items || []).filter((it) => it.crew_id === crewId);
  if (items.length === 0) return null;
  const total = items.length;
  const verified = items.filter((it) => it.status === "manager_verified").length;
  const inProgress = items.filter((it) => it.status === "in_progress").length;
  const awaiting = items.filter((it) => it.status === "completed").length;
  const flagged = items.filter((it) =>
    it.status === "needs_attention" || it.status === "needs_handoff",
  ).length;
  const helpReq = items.filter((it) => it.help_requested && !it.help_accepted_by).length;
  let dot = "bg-slate-300";
  if (flagged > 0 || helpReq > 0) dot = "bg-rose-500";
  else if (awaiting > 0) dot = "bg-amber-500";
  else if (verified === total) dot = "bg-emerald-500";
  else if (inProgress > 0) dot = "bg-indigo-500";
  return { total, verified, inProgress, awaiting, flagged, helpReq, dot };
}

export default function WhosOnTheFloor({
  crewAssignments,
  summary,
  onGoToZoneSheet,
}) {
  const [mode, setMode] = useState("now"); // "now" | "all"
  const nowMin = useMemo(() => nowMinutesInStoreTz(), []);
  const rows = useMemo(() => {
    const all = (crewAssignments || []).filter((r) => r && r.crew_id);
    if (mode === "all") return all;
    return all.filter((r) => isCrewOnFloorNow(r.shift, nowMin));
  }, [crewAssignments, mode, nowMin]);

  // Empty-state chip mode — 2026-02 layout pass. When there's nothing
  // on the floor (or no crew assigned yet), collapse the whole card
  // to a single-row chip instead of rendering the full ~140px card
  // for one line of empty-state text. Full card comes back the
  // moment any crew is assigned.
  const totalAssigned = (crewAssignments || []).filter((r) => r && r.crew_id).length;
  if (totalAssigned === 0) {
    return (
      <EmptyStateChip
        icon={Users}
        tint="slate"
        label="Game Plan"
        message="No crew on the Zone Sheet yet today"
        ctaLabel="Assign"
        onClick={onGoToZoneSheet}
        testid="whos-on-floor-empty-chip"
      />
    );
  }

  return (
    <section
      className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md overflow-hidden ring-1 ring-slate-100"
      data-testid="whos-on-the-floor"
    >
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #0066FF 0%, #06b6d4 50%, #14b8a6 100%)" }} />
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100 flex-wrap gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0"
            style={{ background: "linear-gradient(135deg, #0066FF, #06b6d4)" }}
          >
            <Users weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-blue-600 leading-none">
              Game Plan
            </p>
            <p
              className="text-lg font-black text-slate-900 leading-tight mt-1"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
            >
              Who&apos;s on the Floor
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-1 rounded-full bg-slate-100 p-0.5"
          role="tablist"
          data-testid="whos-on-floor-toggle"
        >
          <button
            type="button"
            onClick={() => setMode("now")}
            className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
              mode === "now" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="whos-on-floor-mode-now"
          >
            Right Now
          </button>
          <button
            type="button"
            onClick={() => setMode("all")}
            className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
              mode === "all" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="whos-on-floor-mode-all"
          >
            Full Day
          </button>
        </div>
      </div>
      <div className="px-4 sm:px-5 py-3">
        {rows.length === 0 ? (
          <p
            className="text-sm text-slate-500 italic py-4 text-center"
            data-testid="whos-on-floor-empty"
          >
            {mode === "now"
              ? "No one currently clocked into the Zone Sheet for this moment. Switch to Full Day to see everyone scheduled today."
              : "No crew on the Zone Sheet yet today."}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((row) => {
              const prog = progressFor(row.crew_id, summary);
              return (
                <li
                  key={row.crew_id}
                  className="py-2.5 flex items-center justify-between gap-3"
                  data-testid={`whos-on-floor-row-${row.crew_id}`}
                >
                  <div className="min-w-0 flex items-center gap-2.5">
                    {prog ? (
                      <span
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${prog.dot}`}
                        title="Task status indicator"
                      />
                    ) : (
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-slate-200" title="No tasks" />
                    )}
                    <div className="min-w-0">
                      <p
                        className="text-sm font-black text-slate-900 truncate flex items-center gap-1.5"
                        data-testid={`whos-on-floor-name-${row.crew_id}`}
                      >
                        <span className="truncate">{row.crew_name || "Crew"}</span>
                        {row.is_visitor && row.home_store_code && (
                          <span
                            className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex-shrink-0"
                            data-testid={`whos-on-floor-visiting-${row.crew_id}`}
                          >
                            Visiting {row.home_store_code}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-2 truncate">
                        {row.shift && (
                          <span className="inline-flex items-center gap-1">
                            <Clock weight="bold" className="w-3 h-3" /> {row.shift}
                          </span>
                        )}
                        {row.coverage && (
                          <span className="truncate">· {row.coverage}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    {prog ? (
                      <p
                        className="text-[11px] font-black tabular-nums text-slate-700"
                        data-testid={`whos-on-floor-progress-${row.crew_id}`}
                      >
                        {prog.verified}/{prog.total} verified
                      </p>
                    ) : (
                      <p className="text-[11px] font-semibold text-slate-400">
                        No tasks
                      </p>
                    )}
                    {prog && prog.awaiting > 0 && (
                      <p className="text-[10px] font-bold text-amber-600">
                        {prog.awaiting} awaiting verify
                      </p>
                    )}
                    {prog && (prog.flagged > 0 || prog.helpReq > 0) && (
                      <p className="text-[10px] font-bold text-rose-600">
                        {prog.flagged + prog.helpReq} need{prog.flagged + prog.helpReq === 1 ? "s" : ""} help
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end">
          <button
            type="button"
            onClick={onGoToZoneSheet}
            className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 hover:text-blue-900 hover:underline"
            data-testid="whos-on-floor-go-to-zone-sheet"
          >
            Manage on Zone Sheet
            <ArrowRight weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
